/**
 * Granny Flat suggestion engine — generate & rank a curated set of ways to STRUCTURE a granny-flat
 * arrangement, optimising the real levers: avoiding unnecessary deprivation (don't pay above the assessed
 * value of the right) and how to handle the home (pay cash from savings vs transfer the home vs sell it).
 * Each candidate is evaluated with the same validated engine the manual path uses and ranked into
 * Best legacy / Best cashflow / Best Age Pension via the shared buildRecommendedOptimalStrategies.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { annuityPreferenceOf, allowsResidual, allowsGuaranteed } from '../../../shared/src/calculation/annuityPreference.ts';
import { buildRecommendedOptimalStrategies, type EvaluatedStrategy, type RecommendedStrategyEntry } from './supportAtHomeRecommendedStrategies.ts';
import { buildFundingVariants, type FundingMap } from './fundingMechanisms.ts';
import { PENSION_RATES } from '../constants/index.ts';
import { formatCurrency } from '../utils/index.ts';
import { payableDebtsOf } from '../features/agedCare/DebtPayoffStrategy.tsx';
import * as calculationApi from '../services/calculationApi.ts';
import { allocateNonConcessionalContribution, nonConcessionalCapacityForHousehold } from '../../../shared/src/calculation/nonConcessionalContributions.ts';
import { purchaseWithinBudget, isStampDutyState } from '../../../shared/src/calculation/stampDuty.ts';
import { ASSET_CLASS_ASSUMPTIONS } from '../../../shared/src/calculation/investmentProfiles.ts';
import { getRecommendedEmergencyFund } from '../utils/emergencyFund.ts';
import type { AnnuityAppetite, AnnuityResidual } from '../../../shared/src/calculation/annuityPreference.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';

const formatAud = (n: number | undefined) => Math.round(n || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

/*
 * THE STRATEGY ANALYSIS SETUP (owner, 2 Oct 2026) — the choices only the client can make, asked
 * once before the run, each only where a candidate would turn on it (the village's pattern):
 *   - what happens to the home: keep it (vacant, or rented out), sell it, transfer it for the
 *     right, or compare every route;
 *   - the state a bought investment property would be in (its duty scale);
 *   - whether a lifetime annuity may be offered, and the most of the freed-up money it may take —
 *     a quarter by default, never all of it;
 *   - the emergency reserve kept in the bank before anything is paid or deployed.
 */
export type GfHomeRoute = 'keep' | 'sell' | 'transfer' | 'all';

export interface GfEngineSetup {
    homeRoute?: GfHomeRoute | null;
    /** Whether the kept home is rented out, and at what weekly rent (the fact-find's likely rent by default). */
    keptHomeRent?: number | null;
    purchaseState?: string | null;
    annuityShareCapPct?: number | null;
    annuityAppetite?: AnnuityAppetite | null;
    annuityResidual?: AnnuityResidual | null;
    emergencyReserve?: number | null;
    /** Moving from a village (8 Oct 2026): the current unit's payout taken as paid at the move (default) or on the state's timeline. */
    oldUnitPaysAtMove?: boolean | null;
    /** The amount the client already plans to pay, where known; 0 = let the engine suggest. */
    knownAmount?: number | null;
    /**
     * What form the known amount takes (2 Oct 2026). 'cash' is tested against Centrelink's
     * reasonable value; 'build' is the cost of constructing the accommodation, which Centrelink takes
     * at face value as the price of the right — no gift, however much it costs (Social Security
     * Guide 4.6.4.50). Absent = cash.
     */
    knownAmountKind?: 'cash' | 'build' | null;
    /** Cash given on top of the build, if any — the "additional assets" the gift test then catches. */
    knownExtraCash?: number | null;
}

/** A quarter by default — "all in" kept winning the cashflow goal on the income alone (the village's lesson, 24 Sep 2026). */
export const DEFAULT_GF_ANNUITY_SHARE_CAP_PCT = 25;
/** The candidates that invest, buy or lock money away need this much freed up — the engine's own gate. */
export const GF_SETUP_SPEND_THRESHOLD = 50_000;
const GF_ANNUITY_MIN = 25_000;

export function gfAnnuityShareCapOf(setup: GfEngineSetup | null | undefined): number {
    const raw = Number(setup?.annuityShareCapPct);
    if (!Number.isFinite(raw) || setup?.annuityShareCapPct == null) return DEFAULT_GF_ANNUITY_SHARE_CAP_PCT;
    return Math.min(100, Math.max(0, raw));
}

/** The saved setup answers, with the fact-find's likely rent and the standard reserve as the fallbacks. */
export function gfEngineSetupFromGoals(factFind: DraftFactFind | null | undefined): GfEngineSetup {
    const goals = (factFind?.goals || {}) as { gfHomeRoute?: string; gfPurchaseState?: string; gfAnnuityShareCapPct?: number; gfEmergencyReserve?: number };
    const homeRoute: GfHomeRoute = goals.gfHomeRoute === 'keep' || goals.gfHomeRoute === 'sell' || goals.gfHomeRoute === 'transfer' || goals.gfHomeRoute === 'all' ? goals.gfHomeRoute : 'all';
    const preference = annuityPreferenceOf(factFind as never);
    return {
        homeRoute,
        keptHomeRent: Number(factFind?.home?.likelyRent) > 0 ? Number(factFind?.home?.likelyRent) : 0,
        purchaseState: typeof goals.gfPurchaseState === 'string' && goals.gfPurchaseState.trim() !== '' ? goals.gfPurchaseState : '',
        annuityShareCapPct: goals.gfAnnuityShareCapPct != null && Number.isFinite(Number(goals.gfAnnuityShareCapPct)) ? Number(goals.gfAnnuityShareCapPct) : DEFAULT_GF_ANNUITY_SHARE_CAP_PCT,
        annuityAppetite: preference.appetite,
        annuityResidual: preference.residual,
        emergencyReserve: Number(goals.gfEmergencyReserve) > 0 ? Number(goals.gfEmergencyReserve) : getRecommendedEmergencyFund(factFind ?? undefined),
        knownAmount: 0,
        oldUnitPaysAtMove: typeof (goals as { gfOldUnitPaysAtMove?: boolean }).gfOldUnitPaysAtMove === 'boolean' ? (goals as { gfOldUnitPaysAtMove?: boolean }).gfOldUnitPaysAtMove : true,
    };
}

export interface GrannyFlatCandidateOptions {
    dateEntered?: string;
    /** The Strategy Analysis Setup answers (2 Oct 2026). Absent: every route, a quarter, the standard reserve. */
    setup?: GfEngineSetup;
    /**
     * A provider's quoted annual annuity income, where the client has one.
     *
     * Threaded into both annuity candidates rather than applied to the winner afterwards: the rate is
     * what the decision turns on, and a real quote can change WHICH candidate wins (owner,
     * 2026-09-01).
     */
    annuityIncomeOverride?: number | null;
    emergencyReserve?: number;
    knownAmount?: number;
    legacyYears?: number;
    currentInputs?: {
        transferHome?: boolean;
        propertyTransferredValue?: number;
        amountPaid?: number;
        constructionCost?: number;
        dateEntered?: string;
        fundFrom?: string;
    };
}

/** The `gf` config passed to the granny-flat calc-engine routes (buildSimulatedFactFindForGrannyFlat etc). */
export interface GrannyFlatConfig {
    /** Moving from a village (8 Oct 2026): the current unit's payout taken as paid at the move (default) or on the state's timeline. */
    oldUnitPaysAtMove?: boolean;
    amountPaid: number;
    propertyTransferredValue: number;
    constructionCost: number;
    dateEntered: string;
    fundFrom: string;
    sellingCosts?: number;
    keptHomeUse?: 'vacant' | 'rented';
    keptHomeRent?: number;
    keptHomeExpenses?: number;
    funding?: FundingMap;
    emergencyReserve?: number;
    deployment?: Record<string, unknown>;
}

export interface GrannyFlatCandidate {
    id: string;
    name: string;
    description: string;
    gf: GrannyFlatConfig;
    /**
     * Debts cleared as part of this candidate — forwarded to the engine's debt payoff plugin, the
     * same route Manual Control uses.
     *
     * The page has rendered a debt payoff card beside its inputs all along and the engine ignored
     * it, so a family could clear a mortgage by hand but was never shown it weighed against paying
     * the assessed value or keeping the money invested. Aged Care and Retirement Village both pair
     * it; this closes the gap.
     */
    debtPayoff?: { payments?: Record<string, number>; funding?: Record<string, number>; repaymentBasis?: 'keep_repayment' | 'lower_repayment' };
    // evaluateGrannyFlatCandidates (calculationApi.ts) takes Record<string, unknown>[] — this index
    // signature lets a typed GrannyFlatCandidate[] satisfy that without a cast at the call site.
    [key: string]: unknown;
}

export interface GrannyFlatSuggestionsResult {
    strategies: EvaluatedStrategy[];
    recommended: RecommendedStrategyEntry[];
    reasonableValue: number;
    /** The do-nothing benchmark, for the panel and the plan summary to measure against. */
    stayPut?: EvaluatedStrategy | null;
    /**
     * Everything a SINGLE candidate needs to be evaluated again, exactly as it was the first time.
     *
     * The deep dive's financial/calendar toggle re-cuts the candidate it is showing, and the only
     * faithful way is to hand the engine the same candidate, the same horizon and the same date
     * (owner, 23 Sep 2026). Carried whole rather than rebuilt at the call site: the evaluated row
     * does not keep everything a candidate was built with (a debt-payoff plan, for one), and the
     * date is derived inside this builder, so a second derivation there would be free to disagree.
     */
    rerun: { candidates: unknown[]; legacyYears: number; date?: string };
}

/** Probe the engine for the assessed reasonable value of the right at this person's age (today). */
export async function getGrannyFlatReasonableValueForClient(factFind: DraftFactFind, dateEntered?: string): Promise<number> {
    const date = dateEntered || new Date().toISOString().slice(0, 10);
    const probe = await calculationApi.buildSimulatedFactFindForGrannyFlat(factFind, { amountPaid: 1, dateEntered: date });
    const dep = await calculationApi.getGrannyFlatDeprivationFromFactFind(probe, PENSION_RATES.couple);
    return dep?.reasonableValue || 0;
}

/**
 * Curated candidate structures (config only). `currentInputs` (from manual control) is included as a
 * "Your current plan" candidate so the client can see whether their intended payment over-pays.
 */
export async function generateGrannyFlatCandidates(factFind: DraftFactFind, options: GrannyFlatCandidateOptions = {}): Promise<GrannyFlatCandidate[]> {
    const dateEntered = options.dateEntered || new Date().toISOString().slice(0, 10);
    const hasHome = (factFind?.home?.value || 0) > 0;
    const homeValue = factFind?.home?.value || 0;
    const reasonableValueRaw = await getGrannyFlatReasonableValueForClient(factFind, dateEntered);
    // Pay the FLOORED reasonable value so we never tip a dollar over it (which would create a tiny gift).
    const fairAmount = Math.floor(reasonableValueRaw);
    const reasonableValueDisplay = Math.round(reasonableValueRaw);
    const setup: GfEngineSetup = options.setup ?? {};
    // Emergency funds to keep in the bank rather than spend on the granny-flat payment (from the setup).
    const reserve = Math.max(0, Number(setup.emergencyReserve ?? options.emergencyReserve) || 0);
    /*
     * WHAT HAPPENS TO THE HOME — the client's choice, from the setup. 'all' (the default) models
     * every route; a chosen route narrows the field to it. The do-not-proceed benchmark, the
     * move-in-only candidate and the client's own plan are route-independent.
     */
    const homeRoute: GfHomeRoute = setup.homeRoute === 'keep' || setup.homeRoute === 'sell' || setup.homeRoute === 'transfer' ? setup.homeRoute : 'all';
    const routeKeep = homeRoute === 'all' || homeRoute === 'keep' || !hasHome;
    const routeSell = hasHome && (homeRoute === 'all' || homeRoute === 'sell');
    const routeTransfer = hasHome && (homeRoute === 'all' || homeRoute === 'transfer');
    /*
     * The plain-words annuity answers: "no" builds no annuity candidate at all; "yes" may narrow
     * the field to one residual version; "not sure" (the default) shows both at the cap. The
     * SETUP's answer wins; where it is silent the CLIENT RECORD is read (annuityPreference.ts).
     */
    const savedPreference = annuityPreferenceOf(factFind as never);
    const appetite: AnnuityAppetite = setup.annuityAppetite === 'none' || setup.annuityAppetite === 'yes' || setup.annuityAppetite === 'unsure' ? setup.annuityAppetite : savedPreference.appetite;
    const residualAnswer: AnnuityResidual = setup.annuityResidual === 'returned' || setup.annuityResidual === 'nil' || setup.annuityResidual === 'guaranteed' || setup.annuityResidual === 'both' ? setup.annuityResidual : savedPreference.residual;
    const gfAnnuityPreference = { appetite, residual: appetite === 'yes' ? residualAnswer : 'both', answered: true } as const;
    const annuityCapPct = appetite === 'none' ? 0 : gfAnnuityShareCapOf(setup);
    /** The property a budget buys once its stamp duty is paid, with the rent struck on the PRICE (the village's rule). */
    const purchaseState = isStampDutyState(setup.purchaseState) ? setup.purchaseState : undefined;
    const propertyFor = (budget: number) => {
        const bought = purchaseWithinBudget(purchaseState, budget);
        const rent = Math.round(bought.price * (ASSET_CLASS_ASSUMPTIONS.propertyYieldPct / 100));
        return { investmentProperty: bought.price, ipStampDuty: bought.duty, ipRent: rent, ipExpenses: Math.round(rent * (ASSET_CLASS_ASSUMPTIONS.propertyExpenseRatioPct / 100)) };
    };
    const dutyWords = (duty: number) => (purchaseState
        ? ` Stamp duty of ${formatAud(duty)} (${purchaseState}) comes out of the proceeds, so the property is sized to what is left.`
        : ' Stamp duty on the property is not modelled — no state was chosen.');

    const candidates: GrannyFlatCandidate[] = [];

    /*
     * 0. DO NOT PROCEED — the benchmark, and the floor every arrangement must beat. Excluded from the
     *    ranked universe and passed as the benchmark instead (see buildGrannyFlatSuggestions).
     */
    candidates.push({
        id: 'gf-stay-put',
        name: 'Do not enter the arrangement',
        description: 'No granny flat arrangement — life as it is today. This is the baseline every option below is measured against.',
        gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: 0, propertyTransferredValue: 0, constructionCost: 0, dateEntered: '', fundFrom: 'liquid' },
        stayPut: true,
    });

    // 1. Pay the assessed (reasonable) value from savings — no gift. The default "clean" structure (home kept,
    //    vacant). The SE COMPARES funding mechanisms: when the payment needs more than cash and the client holds
    //    both shares and an account-based pension, we generate BOTH (draw the ABP — no CGT — vs sell shares — CGT),
    //    and the projection's after-CGT / after-return outcome decides which wins.
    const fundVariants = routeKeep ? buildFundingVariants(factFind, fairAmount, reserve) : [];
    fundVariants.forEach((v) => {
        candidates.push({
            id: `gf-fair-cash${v.key}`,
            name: `${hasHome ? 'Pay the assessed value, keep your home vacant' : 'Pay the assessed value (no gift)'}${v.nameSuffix}`,
            description: `Pay about ${formatAud(reasonableValueDisplay)} — the value Centrelink puts on your lifetime right at your age — from your savings. Nothing counts as a gift.${v.note ? ' ' + v.note : ''}${hasHome ? ' Your former home is kept but left vacant — it becomes an assessable asset.' : ''}`,
            gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: fairAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'savings', keptHomeUse: 'vacant', funding: Object.keys(v.funding).length ? v.funding : undefined, emergencyReserve: reserve },
        });
    });

    // If the client KNOWS the amount they'll pay (from the preliminary setup) and it differs from the assessed
    // value, add it as a candidate — funded from savings, with funding-mechanism variants — so the SE compares
    // "your planned amount" (which may create a gift if above the assessed value) against the no-gift options.
    const knownAmount = Math.round(Number(setup.knownAmount) > 0 ? Number(setup.knownAmount) : (options.knownAmount || 0));
    const knownIsBuild = setup.knownAmountKind === 'build';
    if (routeKeep && knownAmount > 0 && knownIsBuild) {
        /*
         * PAYING FOR THE BUILD (2 Oct 2026). The cost of constructing the accommodation is one of the
         * three forms Centrelink accepts at face value (home title, construction, a property bought in
         * the family's name): no reasonableness test, no gift, however much it costs. Cash on top is
         * "additional assets" — then the whole amount is tested against the greater of the build cost
         * and the formula (shared/src/calculation/grannyFlatArrangement.ts). Both money out is funded
         * from the client's assets, so the funding variants target the total.
         */
        const extraCash = Math.max(0, Math.round(Number(setup.knownExtraCash) || 0));
        const total = knownAmount + extraCash;
        const interest = Math.max(fairAmount, knownAmount);
        const gift = Math.max(0, total - interest);
        buildFundingVariants(factFind, total, reserve).forEach((v) => {
            candidates.push({
                id: `gf-known${v.key}`,
                name: `Pay for the build (${formatAud(knownAmount)})${extraCash > 0 ? ` plus ${formatAud(extraCash)} cash` : ''}${v.nameSuffix}`,
                description: `Pay ${formatAud(knownAmount)} to build or extend the family's home for your accommodation, from your savings. Centrelink takes the cost of the build as the price of the right, so on its own none of it is a gift${knownAmount > fairAmount ? ` — even though it is above the formula value of ${formatAud(reasonableValueDisplay)}` : ''}.${extraCash > 0 ? ` The ${formatAud(extraCash)} given on top is tested: the right is valued at ${formatAud(interest)}, so ${gift > 0 ? `about ${formatAud(gift)} is treated as a gift and counted for 5 years` : 'nothing counts as a gift'}.` : ''}${v.note ? ' ' + v.note : ''}`,
                gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: extraCash, propertyTransferredValue: 0, constructionCost: knownAmount, dateEntered, fundFrom: 'savings', keptHomeUse: 'vacant', funding: Object.keys(v.funding).length ? v.funding : undefined, emergencyReserve: reserve },
            });
        });
    } else if (routeKeep && knownAmount > 0 && Math.abs(knownAmount - fairAmount) > 1) {
        const overAssessed = knownAmount > fairAmount;
        buildFundingVariants(factFind, knownAmount, reserve).forEach((v) => {
            candidates.push({
                id: `gf-known${v.key}`,
                name: `Pay your planned amount (${formatAud(knownAmount)})${v.nameSuffix}`,
                description: `Pay the ${formatAud(knownAmount)} you plan to give in cash, from your savings.${overAssessed ? ` This is above Centrelink's reasonable value (${formatAud(reasonableValueDisplay)}), so about ${formatAud(knownAmount - fairAmount)} is treated as a gift and counted for 5 years.` : ` This is at or below the reasonable value (${formatAud(reasonableValueDisplay)}), so nothing counts as a gift.`}${v.note ? ' ' + v.note : ''}`,
                gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: knownAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'savings', keptHomeUse: 'vacant', funding: Object.keys(v.funding).length ? v.funding : undefined, emergencyReserve: reserve },
            });
        });
    }

    if (hasHome && routeKeep) {
        // 1b. Same, but RENT OUT the kept former home. Once you move into the granny flat the former home is an
        // assessable asset whether or not it's rented — so renting it is usually pure extra cashflow/estate (the
        // pension is already affected by the asset). Uses the setup's rent, else the client's likely rent (or ~3.5% gross yield).
        const weeklyRent = Math.round(Number(setup.keptHomeRent) > 0 ? Number(setup.keptHomeRent) : (factFind?.home?.likelyRent || (homeValue * 0.035) / 52));
        const annualExpenses = Math.round(factFind?.home?.likelyExpenses || (weeklyRent * 52 * 0.25));
        candidates.push({
            id: 'gf-keep-rent',
            name: 'Pay the assessed value and rent out your home',
            description: `Pay the assessed value from savings (no gift), keep your former home and rent it out for about ${formatAud(weeklyRent * 52)}/yr. The home is assessable either way, so the rent is extra income. Heads-up: the main-residence CGT exemption lasts up to 6 years after you move out — after that, capital gains tax can apply when you sell (you can only have one main residence at a time).`,
            gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: fairAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'savings', keptHomeUse: 'rented', keptHomeRent: weeklyRent, keptHomeExpenses: annualExpenses, emergencyReserve: reserve },
        });
    }

    if (routeTransfer) {
        // 2. Transfer the home for the right — no gift: the title on its own is taken at face value as
        //    the price of the right (Social Security Guide 4.6.4.50), whatever the home is worth.
        candidates.push({
            id: 'gf-transfer-home',
            name: 'Transfer your home for the right',
            description: 'Sign your home over to the family in return for the lifetime right to live there. Centrelink takes the home\'s value as the price of the right, so none of it is a gift — however much the home is worth — and you stay a homeowner for the pension.',
            gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: 0, propertyTransferredValue: homeValue, constructionCost: 0, dateEntered, fundFrom: 'savings' },
        });
    }

    if (routeSell) {
        // 3. Sell the home and pay the assessed value — frees capital but leaves more assessable cash.
        candidates.push({
            id: 'gf-sell-pay-fair',
            name: 'Sell your home and pay the assessed value',
            description: 'Sell the home, pay the assessed value for your right (no gift), and keep the rest in the bank — more cash on hand, but more is counted for the pension.',
            gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: fairAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'sell_home' },
        });

        // 4-6. Sell the home, pay the assessed value, and deploy the residual: shares / IP / annuity.
        const sellCosts = Math.round(homeValue * 0.025);
        /*
         * THE EMERGENCY RESERVE applies to the sale route too (owner, 2 Oct 2026): what the bank
         * already holds counts towards it, and only the shortfall is held back from the proceeds.
         */
        const bankToday = (factFind.assets?.bankAccounts || []).reduce((sum, a) => sum + (Number(a?.value) || 0), 0);
        const reserveTopUp = Math.max(0, reserve - bankToday);
        const residualAfterPay = Math.max(0, homeValue - sellCosts - fairAmount - reserveTopUp);
        const reserveWords = reserveTopUp > 0 ? ` ${formatAud(reserveTopUp)} of the proceeds stays in the bank as the emergency reserve.` : '';
        if (residualAfterPay >= GF_SETUP_SPEND_THRESHOLD) {
            // Pin sellingCosts so the deployed amount equals the scenario's residual exactly (no rounding over-deploy).
            const baseSell = { amountPaid: fairAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'sell_home', sellingCosts: sellCosts };

            candidates.push({
                id: 'gf-sell-invest',
                name: 'Sell your home and invest the rest',
                description: `Sell the home, pay the assessed value for your right (no gift), and put the freed-up money into a diversified share / managed-fund portfolio instead of the bank — aiming for higher long-term growth and income (still counted for the pension; returns aren't guaranteed).${reserveWords}`,
                gf: { ...baseSell, deployment: { shares: residualAfterPay } },
            });

            const ip = propertyFor(residualAfterPay);
            if (ip.investmentProperty > 0) candidates.push({
                id: 'gf-sell-property',
                name: 'Sell your home and buy an investment property',
                description: `Sell the home, pay the assessed value for your right (no gift), and use the freed-up money to buy an investment property for rental income and potential growth. The property is an assessable asset and the net rent counts for the pension.${dutyWords(ip.ipStampDuty)}${reserveWords}`,
                gf: { ...baseSell, deployment: ip },
            });

            /*
             * BOTH ENDS OF THE RESIDUAL-CAPITAL TRADE-OFF, or the strategy is disqualified rather
             * than weighed.
             *
             * Centrelink assesses a lifetime annuity at 60% of its purchase price WHATEVER its
             * residual capital value — RCV changes the payment rate, not the assets-test treatment.
             * But net worth values an annuity at its RESIDUAL (assessment/netWorth.ts, deliberately:
             * a nil-RCV lifetime annuity really does leave nothing behind), so a nil-RCV candidate
             * loses the legacy ranking by its full purchase price on day one. It was never being
             * compared against the alternatives; it was being ruled out before the comparison began.
             *
             * The Downsizer, Retirement Village, Support at Home and Aged Care engines were all fixed
             * for this. This module and Cashflow & Age Pension were built afterwards and inherited
             * the original single-variant shape — which is exactly why the guard now covers every
             * module in annuityResidualCapitalVariants.test.ts rather than the four it started with.
             */
            const annuityMeansTest = 'only 60% of the purchase price counted for the assets test (for the first five years, or until age 85 if that is later, and 30% after that), which often lifts the Age Pension';
            // NEVER all of it: the setup's share goes into the annuity, the rest into shares.
            const annuityShare = Math.round(residualAfterPay * (annuityCapPct / 100));
            const annuityRest = Math.max(0, residualAfterPay - annuityShare);
            const restWords = annuityRest > 0 ? ` The remaining ${formatAud(annuityRest)} goes into shares.` : '';
            if (annuityShare >= GF_ANNUITY_MIN && allowsResidual(gfAnnuityPreference, 0)) candidates.push({
                id: 'gf-sell-annuity',
                name: 'Sell your home and buy a lifetime annuity (nil residual)',
                description: `Sell the home, pay the assessed value for your right (no gift), and convert ${formatAud(annuityShare)} of the freed-up money into a lifetime annuity — guaranteed income for life, with ${annuityMeansTest}. This version pays the most income and leaves nothing of the purchase price to the estate.${restWords}${reserveWords}`,
                gf: { ...baseSell, deployment: { annuity: annuityShare, annuityType: 'lifetime', annuityResidualPercent: 0, annuityIncomeOverride: options.annuityIncomeOverride ?? undefined, ...(annuityRest > 0 ? { shares: annuityRest } : {}) } },
            });
            if (annuityShare >= GF_ANNUITY_MIN && allowsResidual(gfAnnuityPreference, 1)) candidates.push({
                id: 'gf-sell-annuity-rcv',
                name: 'Sell your home and buy a lifetime annuity (capital returned)',
                description: `The same annuity, but with the purchase price coming back to the estate on an early death — in full within the first half of life expectancy, less after that — rather than kept by the provider. It pays less income, and Centrelink still counts only 60% either way.${restWords}${reserveWords}`,
                gf: { ...baseSell, deployment: { annuity: annuityShare, annuityType: 'lifetime', annuityResidualPercent: 100, annuityIncomeOverride: options.annuityIncomeOverride ?? undefined, ...(annuityRest > 0 ? { shares: annuityRest } : {}) } },
            });
            if (annuityShare >= GF_ANNUITY_MIN && allowsGuaranteed(gfAnnuityPreference, factFind as never)) candidates.push({
                id: 'gf-sell-annuity-guaranteed',
                name: 'Sell your home and buy a lifetime annuity (full amount guaranteed)',
                description: `A lifetime annuity and a life policy bought together, so the full purchase price comes back to the estate whenever death comes. It pays the least income of the three, and only the annuity half earns the 60% assets-test treatment; the policy is counted at what it cost.${restWords}${reserveWords}`,
                gf: { ...baseSell, deployment: { annuity: annuityShare, annuityType: 'guaranteed', annuityResidualPercent: 100, annuityIncomeOverride: options.annuityIncomeOverride ?? undefined, ...(annuityRest > 0 ? { shares: annuityRest } : {}) } },
            });

            /*
             * 7-8. AFTER-TAX SUPER (F.6) AND AN INVESTMENT BOND (F.5) for the residual — 17 Sep 2026.
             * Owner-directed: a granny flat funded by selling the home leaves residual money too, and
             * it deserves the same homes the Downsizer offers. Logic-based: super only where the
             * person may still contribute (under 75, room under the balance bands), the bond as the
             * tax-paid alternative where they may not, or for what the caps cannot take.
             */
            const ff = factFind as unknown as FactFind;
            const superRoom = nonConcessionalCapacityForHousehold(ff);
            const bondBlurb = 'An investment bond pays its own tax inside (30%) and nothing on your tax return; after ten years withdrawals are tax-free. Centrelink counts it in full and deems it, like shares, so this is about tax, not the pension.';
            if (superRoom.total > 0) {
                const ncc = allocateNonConcessionalContribution(ff, residualAfterPay);
                const nccTotal = ncc.client + ncc.partner;
                if (nccTotal >= 25_000) {
                    candidates.push({
                        id: 'gf-sell-super',
                        name: 'Sell your home, pay the assessed value, and top up super after tax',
                        description: `Sell the home, pay the assessed value for your right (no gift), and put ${formatAud(nccTotal)} of the rest into super as an after-tax contribution${ncc.preferred ? ` — ${ncc.preferred === 'client' ? (factFind?.client?.firstName || 'the client') : (factFind?.partner?.firstName || 'the partner')} first, whose accumulation super is not counted by Centrelink until Age Pension age` : ''}. Capped at what each of you may contribute now; anything above the cap goes into shares.`,
                        gf: { ...baseSell, deployment: { superContribution: ncc.client, partnerSuperContribution: ncc.partner, superCommenceIncomeStream: ncc.preferred == null, shares: Math.max(0, residualAfterPay - nccTotal) } },
                    });
                }
                if (residualAfterPay - superRoom.total >= GF_SETUP_SPEND_THRESHOLD) {
                    candidates.push({
                        id: 'gf-sell-super-and-bond',
                        name: 'Sell your home, fill super after tax, and bond the rest',
                        description: `Put ${formatAud(nccTotal)} into super as an after-tax contribution — the most the caps allow — and the remaining ${formatAud(residualAfterPay - nccTotal)} into an investment bond. ${bondBlurb}`,
                        gf: { ...baseSell, deployment: { superContribution: ncc.client, partnerSuperContribution: ncc.partner, superCommenceIncomeStream: ncc.preferred == null, investmentBond: Math.max(0, residualAfterPay - nccTotal) } },
                    });
                }
            } else {
                candidates.push({
                    id: 'gf-sell-bond',
                    name: 'Sell your home, pay the assessed value, and bond the rest',
                    description: `Sell the home, pay the assessed value for your right (no gift), and put the remaining ${formatAud(residualAfterPay)} into an investment bond — the tax-paid home for money super can no longer take. ${bondBlurb}`,
                    gf: { ...baseSell, deployment: { investmentBond: residualAfterPay } },
                });
            }
        }
    }

    // 4. The client's current plan (if they've entered a meaningful arrangement) — for comparison.
    const ci = options.currentInputs;
    if (ci) {
        const ciProperty = ci.transferHome ? homeValue : (ci.propertyTransferredValue || 0);
        const ciCash = ci.amountPaid || 0;
        if (ciCash + ciProperty + (ci.constructionCost || 0) > 0) {
            candidates.push({
                id: 'gf-your-plan',
                name: 'Your current plan',
                description: 'The arrangement you entered in Manual Control, for comparison.',
                gf: {
                    amountPaid: ciCash,
                    propertyTransferredValue: ciProperty,
                    constructionCost: ci.constructionCost || 0,
                    dateEntered: ci.dateEntered || dateEntered,
                    fundFrom: ci.transferHome ? 'savings' : (ci.fundFrom || 'savings'),
                },
            });
        }
    }

    /*
     * MOVE IN AND PAY NOTHING — the option every family raises first, and the engine never modelled.
     *
     * This is NOT the same thing as the do-nothing benchmark, which is not moving at all. Here the
     * client moves in with family without paying for the right to live there: no granny-flat
     * interest is created, so nothing counts as a gift and there is no reasonableness test to
     * satisfy. It looks like the cheapest option on the page, and it usually is in cash terms.
     *
     * What it does NOT avoid is the consequence families are most surprised by. Centrelink exempts
     * ONE home, and only while you live in it. Move out and the house you still own becomes an
     * ordinary counted asset — on the case this was written against, that alone takes a $29,824
     * pension to nil and turns a $9,176 cashflow gap into $39,000. Paying nothing does not protect
     * the pension; living somewhere else is what costs it.
     *
     * So this is offered as a candidate precisely so the ranking can show that. An engine that only
     * ever priced arrangements with a payment left the family to discover the free option's cost on
     * their own.
     */
    candidates.push({
        id: 'gf-move-in-only',
        name: 'Move in without paying for the right',
        description:
            'Move in with family and pay nothing for the right to live there. No granny-flat interest is created, so nothing counts as a gift and there is no assessed value to meet — but the home you leave stops being exempt the moment you stop living in it, and is counted like any other asset from then on. Rent Assistance generally does not apply when living with family.',
        gf: {
            amountPaid: 0,
            propertyTransferredValue: 0,
            constructionCost: 0,
            dateEntered,
            fundFrom: 'liquid',
        },
    });

    /*
     * DEBT CLEARANCE — paired against every arrangement above, the same pattern the aged care and
     * village engines use. The interest stops and the repayments leave the cashflow, but the money
     * spent is money that cannot also pay the family for the right to live there — which for a
     * household doing both at once is exactly the competition worth ranking.
     *
     * The do-nothing benchmark is left pure: it has to stay indistinguishable from the client's life
     * as it is, or nothing else has anything to be measured against.
     *
     * Below $5,000 a payoff cannot move a ranking and each twin costs a full projection — the same
     * materiality floor as the other modules.
     */
    const debts = payableDebtsOf(factFind);
    const totalDebt = debts.reduce((sum, d) => sum + d.balance, 0);
    if (totalDebt >= 5_000) {
        const payments = Object.fromEntries(debts.map((d) => [d.key, d.balance]));
        const twins = candidates
            .filter((c) => c.stayPut !== true)
            .map((c) => ({
                ...c,
                id: `${c.id}-clear-debt`,
                name: `${c.name}, and clear your debts`,
                description: `${c.description} This version also clears your outstanding debts of ${formatCurrency(totalDebt)} at the same time — the interest stops and the repayments leave your cashflow, but that money is no longer available for anything else in the plan.`,
                debtPayoff: { payments, repaymentBasis: 'keep_repayment' as const },
            }));
        candidates.push(...twins);
    }

    return candidates;
}

/**
 * Evaluate every candidate with the engine and rank into goal leaders.
 */
export async function buildGrannyFlatSuggestions(factFind: DraftFactFind, options: GrannyFlatCandidateOptions = {}): Promise<GrannyFlatSuggestionsResult> {
    const legacyYears = options.legacyYears || 10;
    const dateEntered = options.dateEntered || new Date().toISOString().slice(0, 10);
    const reasonableValue = Math.round(await getGrannyFlatReasonableValueForClient(factFind, dateEntered));
    const candidates = await generateGrannyFlatCandidates(factFind, { ...options, dateEntered });

    // One request evaluates every candidate against the engine server-side (see
    // server/src/rest/calculation/suggestionEngine/grannyFlat.ts) instead of 3 calls x N candidates.
    // A FUTURE entry date defers every candidate to the same day as the manual run — timing belongs
    // to the case. Today or the past means the arrangement starts now, the long-standing behaviour.
    const today = new Date().toISOString().slice(0, 10);
    const futureEntryDate = options.dateEntered && options.dateEntered > today ? options.dateEntered : undefined;
    const strategies: EvaluatedStrategy[] = await calculationApi.evaluateGrannyFlatCandidates(factFind, candidates, legacyYears, futureEntryDate);

    // Ranked among the real arrangements, each measured against not proceeding — so nothing is
    // recommended that leaves the client worse off than doing nothing.
    const stayPut = strategies.find((s) => s.id === 'gf-stay-put') || null;
    const arrangements = strategies.filter((s) => s.id !== 'gf-stay-put');
    const recommended = buildRecommendedOptimalStrategies(arrangements, stayPut);
    return { strategies, recommended, reasonableValue, stayPut, rerun: { candidates, legacyYears, date: futureEntryDate } };
}
